import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import ErrorCdMsgConstants from '../constants/error-cd-msg.constants';

/**
 * アラートメッセージストア。
 */
@Module({ namespaced: true })
export default class AlertMessageStore extends VuexModule {
  /** グローバル成功メッセージリスト。 */
  _successMessages = Array<string>();
  /** グローバルエラーメッセージリスト。 */
  _errorMessages = Array<string>();

  /**
   * グローバル成功メッセージマップにメッセージを追加します。
   *
   * @param message 成功メッセージ
   */
  @Mutation
  public setSuccessMessage(message: string) {
    this._successMessages.push(message);
  }

  /**
   * グローバルエラーメッセージマップにメッセージを追加します。
   *
   * @param message エラーメッセージ
   */
  @Mutation
  public setErrorMessage(message: string) {
    this._errorMessages.push(message);
  }

  /**
   * グローバル成功メッセージマップからメッセージを削除します。
   *
   * @param message 成功メッセージ
   */
  @Mutation
  public removeSuccessMessage(message: string) {
    const foundIndex = this._successMessages.indexOf(message);
    if (foundIndex !== -1) {
      this._successMessages.splice(foundIndex, 1);
    }
  }

  /**
   * グローバルエラーメッセージマップからメッセージを削除します。
   *
   * @param message エラーメッセージ
   */
  @Mutation
  public removeErrorMessage(message: string) {
    const foundIndex = this._errorMessages.indexOf(message);
    if (foundIndex !== -1) {
      this._errorMessages.splice(foundIndex, 1);
    }
  }

  @Mutation
  public removeAllErrorMessages() {
    this._errorMessages = [];
  }

  @Mutation
  public removeAllSuccessMessages() {
    this._successMessages = [];
  }

  @Mutation
  public removeDisconnectionMessage() {
    const disconnectMsgIndex = this._errorMessages.findIndex((msg) =>
      ErrorCdMsgConstants.SERVER_DISCONNECTION_ERR_CD_LIST.some((errCd) => msg.includes(errCd))
    );
    if (disconnectMsgIndex !== -1) {
      this._errorMessages.splice(disconnectMsgIndex, 1);
    }
  }

  /**
   * グローバル成功メッセージマップにメッセージを追加します。
   *
   * @param message 成功メッセージ
   * @returns メッセージがグローバルメッセージマップに存在しない場合はtrue、それ以外の場合はfalse
   */
  @Action({ rawError: true })
  public addToSuccessMessageList(message: string): void {
    // 切断メッセージが表示されている場合、新しいメッセージをリストに追加しない
    const isDisplayingDisconnectMsg = this._errorMessages.some((msg) =>
      ErrorCdMsgConstants.SERVER_DISCONNECTION_ERR_CD_LIST.some((errCd) => msg.includes(errCd))
    );
    if (isDisplayingDisconnectMsg) {
      return;
    }
    this.context.commit('removeAllErrorMessages');
    this.context.commit('removeAllSuccessMessages');
    this.context.commit('setSuccessMessage', message);
  }

  /**
   * グローバルエラーメッセージマップにメッセージを追加します。
   *
   * @param message エラーメッセージ
   * @returns メッセージがグローバルメッセージマップに存在しない場合はtrue、それ以外の場合はfalse
   */
  @Action({ rawError: true })
  public addToErrorMessageList(message: string): void {
    // 切断メッセージが表示されている場合、新しいメッセージをリストに追加しない
    const notNewDisconnectMsg = ErrorCdMsgConstants.SERVER_DISCONNECTION_ERR_CD_LIST.every(
      (errCd) => !message.includes(errCd)
    );
    const isDisplayingDisconnectMsg = this._errorMessages.some((msg) =>
      ErrorCdMsgConstants.SERVER_DISCONNECTION_ERR_CD_LIST.some((errCd) => msg.includes(errCd))
    );
    if (notNewDisconnectMsg && isDisplayingDisconnectMsg) {
      return;
    }
    this.context.commit('removeAllErrorMessages');
    this.context.commit('setErrorMessage', message);
  }

  /**
   * グローバル成功メッセージマップからメッセージを削除します。
   *
   * @param message 成功メッセージ
   */
  @Action({ rawError: true })
  public removeMessageFromSuccessList(message: string): void {
    this.context.commit('removeSuccessMessage', message);
  }

  /**
   * グローバルエラーメッセージマップからメッセージを削除します。
   *
   * @param message エラーメッセージ
   */
  @Action({ rawError: true })
  public removeMessageFromErrorList(message: string): void {
    this.context.commit('removeErrorMessage', message);
  }

  @Action({ rawError: true })
  public removeAllErrorMessagesFromList(): void {
    this.context.commit('removeAllErrorMessages');
  }

  @Action({ rawError: true })
  public removeAllMessages(): void {
    this.context.commit('removeAllErrorMessages');
    this.context.commit('removeAllSuccessMessages');
  }

  @Action({ rawError: true })
  public removeDisconnectMessage(): void {
    this.context.commit('removeDisconnectionMessage');
  }

  @Action({ rawError: true })
  public removeAllSuccessMessagesFromList(): void {
    this.context.commit('removeAllSuccessMessages');
  }

  get errorMessageArray(): Array<string> {
    return this._errorMessages;
  }

  get successMessageArray(): Array<string> {
    return this._successMessages;
  }
}
